import { Notification } from '@thanx/uikit/notification'
import { Select } from '@thanx/uikit/select'
import { Text } from '@thanx/uikit/text'
import { useResolveEscalationMutation } from 'api/recoveryAi'
import type { EscalationResolutionType } from 'api/recoveryAi.types'
import Input from 'components/Form/Input'
import ModalContainer from 'components/ModalContainer'
import Form from 'formsy-react'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'

const t = buildTranslate('recovery_ai.case_detail.escalation.resolve_modal')

// The resolution dropdown renders in its own layer that must stack ABOVE the
// modal (ModalContainer defaults to zIndex 3), matching the GrantRewardModal
// Select-in-ModalContainer pattern.
const RESOLUTION_DROPDOWN_Z_INDEX = 1002

// Operator-facing resolution outcomes. `resolved_by_rai` is intentionally omitted
// — it's an AI outcome, not an operator choice — though the wire enum still
// accepts it (see EscalationResolutionType). F4 / BE reviewer's flagged concern.
const RESOLUTION_OPTIONS: EscalationResolutionType[] = [
  'resolved_manually',
  'unresolved',
]

type Props = {
  isOpen: boolean
  escalationId: string
  onClose: () => void
}

// Operator action: resolve an escalation with a resolution outcome + optional
// note. NON-money (a pure AASM transition). Reuses the shared ModalContainer.
const ResolveEscalationModal = ({ isOpen, escalationId, onClose }: Props) => {
  const [css] = useStyletron()
  const ability = useAbility()
  const [resolveEscalation, { isLoading, isError, reset }] =
    useResolveEscalationMutation()
  const [resolutionType, setResolutionType] =
    useState<EscalationResolutionType | null>(null)
  const [notes, setNotes] = useState('')

  const options = RESOLUTION_OPTIONS.map(value => ({
    value,
    label: t(`resolution_types.${value}`),
  }))

  const canProceed = !!resolutionType && !isLoading

  // Defense-in-depth (D6): CaseDetail only mounts this for managers, but re-check
  // so the write control can never render for a view-only operator.
  if (ability.cannot('manage', 'MerchantSettings')) return null

  const handleClose = () => {
    setResolutionType(null)
    setNotes('')
    // Clear any failed-resolve error so a stale banner doesn't show on reopen —
    // this modal instance is mounted persistently (only isOpen toggles).
    reset()
    onClose()
  }

  const handleConfirm = () => {
    if (!resolutionType) return
    const trimmed = notes.trim()
    // Body is exactly { resolution_type, notes? } — non-money, no amount_cents.
    resolveEscalation({
      id: escalationId,
      resolution_type: resolutionType,
      ...(trimmed ? { notes: trimmed } : {}),
    })
      .unwrap()
      .then(handleClose)
      // Surfaced inline via isError below; swallow so it isn't an unhandled rejection.
      .catch(() => {})
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={t('heading')}
      ariaLabel={t('aria_label')}
      proceedText={t('confirm')}
      onProceed={handleConfirm}
      canProceed={canProceed}
      isLoading={isLoading}
    >
      <Form className="mt-m">
        <Text.BodyText4 tag="p" bold className="mb-xs">
          {t('resolution_type_label')}
        </Text.BodyText4>
        <Select
          aria-label={t('resolution_type_label')}
          options={options}
          value={options.find(option => option.value === resolutionType)}
          onChange={(selected: { value: EscalationResolutionType }[]) =>
            setResolutionType(selected[0] ? selected[0].value : null)
          }
          placeholder={t('resolution_type_label')}
          zIndex={RESOLUTION_DROPDOWN_Z_INDEX}
        />

        <Input
          name="notes"
          componentClass="textarea"
          label={t('notes_label')}
          placeholder={t('notes_placeholder')}
          inputProps={{ rows: 4 }}
          value={notes}
          onChange={event => setNotes(event.currentTarget.value)}
          className="mt-m mb-xs"
        />
      </Form>

      {isError && (
        <div className={css({ marginTop: '12px' })}>
          <Notification kind="negative" size="full" closeable onClose={reset}>
            {t('error')}
          </Notification>
        </div>
      )}
    </ModalContainer>
  )
}

export default ResolveEscalationModal
